import { useAddProductVariantToCartMutation } from '@graphql/mutations/__generated__/product-reflection.generated'
import { ProductCardVariantQuery } from '@graphql/queries/__generated__/product-card.generated'
import AddShoppingCartIcon from '@icons/AddShoppingCartIcon'
import PdfFileIcon from '@icons/PdfFileIcon'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_PRODUCT_QTY } from '../../../../constants'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import { formErrorsToString } from '../../../misc/utils'
import { checkVariantAvailability } from '../../../utils/misc'
import KitQuantityStepper from '../VariantKitPanel/KitQuantityStepper'
import {
  AddedToCartResult,
  ExtraRow,
  multiplyPriceRange,
  Price,
  PriceRow,
  pushAddToCartEvent,
  SubmitButton,
  updateCacheAfterAdd,
} from './common'

type Variant = ProductCardVariantQuery['productVariant']

export const isDigital = (variant: { isSupply: boolean; shippingRequired: boolean }) =>
  !variant.isSupply && !variant.shippingRequired

interface Props {
  variant: Variant
  productName: string
  isArchive: boolean
  categoryName?: string

  onAdded(result: AddedToCartResult): void
}

// Form for a single (non-kit) variant: a digital pattern is added once, a physical item by quantity.
const VariantSelection: React.FC<Props> = ({ variant, productName, isArchive, categoryName, onAdded }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [quantity, setQuantity] = useState(1)
  const [adding, setAdding] = useState(false)
  const digital = isDigital(variant)

  const [addVariantToCart] = useAddProductVariantToCartMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => {
      if (!data?.addVariantToCart.errors.length) updateCacheAfterAdd(cache, data?.addVariantToCart)
    },
  })

  const isAvailable = checkVariantAvailability(variant) && !isArchive
  // A digital pattern can be bought once; a physical item is limited by stock unless it isn't tracked (backorder).
  const inCartDigital = digital && variant.isInCart
  const maxQuantity = variant.stockQuantity > 0 ? Math.min(variant.stockQuantity, MAX_PRODUCT_QTY) : MAX_PRODUCT_QTY

  const onAddToCart = async () => {
    setAdding(true)
    try {
      const { data } = await addVariantToCart({ variables: { pk: variant.pk, quantity } })
      const result = data?.addVariantToCart
      const errors = result?.errors || []
      if (errors.length) {
        enqueueSnackbar(formErrorsToString(errors), { variant: 'error' })
        setAdding(false)
        return
      }
      pushAddToCartEvent(result, {
        name: productName,
        categoryName,
        currency: variant.priceRange.currency,
        unitPrice: variant.priceRange.discount.min,
      })
      // The panel covers this form with AddedToCartNotice and then closes, so no state updates after this point.
      onAdded(result)
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
      setAdding(false)
    }
  }

  const buttonLabel = () => {
    if (inCartDigital) return t('button.alreadyInTheBag', 'Already in the bag')
    if (!isAvailable && !isArchive) return t('productOutOfStock', 'Out of Stock')
    return digital
      ? t('productCard.addPdfPatternToCart', 'Add PDF Pattern to Cart')
      : t('button.addToCart', 'Add to Cart')
  }

  return (
    <>
      {digital ? (
        <PriceRow>
          <Price range={variant.priceRange} />
        </PriceRow>
      ) : (
        <div className='product-card-panel__price-row product-card-panel__price-row--stepper'>
          <KitQuantityStepper quantity={quantity} availableQuantity={maxQuantity} onChangeQuantity={setQuantity} />
          <Price range={multiplyPriceRange(variant.priceRange, quantity)} />
        </div>
      )}
      <ExtraRow>
        {digital && variant.filesCount > 0 && (
          <p className='product-card-panel__info'>
            {t('productCard.filesInProduct', '{{count}} files in product', { count: variant.filesCount })}
          </p>
        )}
        {!digital && variant.stockStatus === OutOfStockStatusEnum.Backorder && (
          <p className='product-card-panel__info product-card-panel__muted'>{t('productBackorder', 'Backorder')}</p>
        )}
      </ExtraRow>
      <SubmitButton
        loading={adding}
        icon={digital ? <PdfFileIcon viewBox='0 0 400 400' /> : <AddShoppingCartIcon viewBox='0 -960 960 960' />}
        disabled={!isAvailable || inCartDigital || adding}
        onClick={onAddToCart}
      >
        {buttonLabel()}
      </SubmitButton>
    </>
  )
}

export default VariantSelection
